import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import {
  DEFAULT_INPUTS,
  LIMITS,
  calculate,
  formatHours,
  formatMoney,
  formatPayback,
  inputsFromSearch,
  searchFromInputs,
} from './reportingCalculator';
import type { CalculatorKey } from './reportingCalculator';

const SITE_URL = 'https://www.greyfielddata.com';
const URL_PATH = '/tools/reporting-time-calculator';

interface Field {
  key: CalculatorKey;
  label: string;
  help: string;
  prefix?: string;
  suffix?: string;
  step: number;
}

const groups: { heading: string; fields: Field[] }[] = [
  {
    heading: 'Manual reporting today',
    fields: [
      { key: 'reports', label: 'Recurring reports', help: 'Reports someone rebuilds or refreshes by hand.', step: 1 },
      { key: 'hoursPerRun', label: 'Hours to produce each one', help: 'Exporting, pasting, cleaning, and checking.', suffix: 'hours', step: 0.25 },
      { key: 'runsPerMonth', label: 'Times each is produced per month', help: 'Weekly is about 4. Daily is about 22 working days.', step: 1 },
      { key: 'fixHoursPerMonth', label: 'Hours spent reconciling per month', help: 'Chasing numbers that disagree, answering "why is this different?"', suffix: 'hours', step: 1 },
      { key: 'hourlyCost', label: 'Loaded hourly cost of the people doing it', help: 'Salary plus benefits and overhead, divided by working hours.', prefix: '$', step: 5 },
    ],
  },
  {
    heading: 'After automation',
    fields: [
      { key: 'automationShare', label: 'Share of that time automation removes', help: 'Someone still reviews the output. 60 to 80% is a common planning range.', suffix: '%', step: 5 },
      { key: 'buildCost', label: 'One-time build cost', help: 'Pipelines, models, and dashboards, whether built in-house or by a partner.', prefix: '$', step: 500 },
      { key: 'monthlyUpkeep', label: 'Monthly upkeep', help: 'Added tool fees plus maintenance time.', prefix: '$', step: 50 },
    ],
  },
];

type Draft = Record<CalculatorKey, string>;

function toDraft(values: Record<CalculatorKey, number>): Draft {
  return Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value)])) as Draft;
}

export default function ReportingCalculatorPage() {
  const [draft, setDraft] = React.useState<Draft>(() => toDraft(DEFAULT_INPUTS));
  const [edited, setEdited] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const result = calculate(draft);

  React.useEffect(() => {
    setDraft(toDraft(inputsFromSearch(window.location.search)));
  }, []);

  // Only touch the address bar after the visitor changes a value, and debounce it, so a
  // plain visit is not rewritten (and analytics does not log extra history page views).
  // Keeps the router's history state, the hash, and query params the calculator does not own.
  React.useEffect(() => {
    if (!edited) return;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      new URLSearchParams(searchFromInputs(draft)).forEach((value, key) => params.set(key, value));
      window.history.replaceState(window.history.state, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
    }, 600);
    return () => window.clearTimeout(timer);
  }, [draft, edited]);

  function update(key: CalculatorKey, value: string) {
    setCopied(false);
    setEdited(true);
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(`${SITE_URL}${URL_PATH}${searchFromInputs(draft)}`);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const { input } = result;

  return (
    <BlogLayout wide>
      <SEO
        title="Reporting time calculator: what manual reports cost"
        description="Estimate the hours and cost your team spends building and reconciling reports by hand, and how long automating them would take to pay back."
        url={`${SITE_URL}${URL_PATH}`}
      />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: 'Reporting time calculator', url: `${SITE_URL}${URL_PATH}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">Reporting time calculator</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <h1 className="text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">Reporting time calculator</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">Put a number on the time your team spends producing and reconciling reports by hand, then see whether automating them pays back. Change any value and the results update.</p>
      </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_400px]">
        <form className="space-y-8" onSubmit={(event) => event.preventDefault()}>
          {groups.map((group) => (
            <fieldset key={group.heading} className="rounded-2xl border border-slate-200 p-5 sm:p-7">
              <legend className="px-2 text-lg font-semibold text-slate-950">{group.heading}</legend>
              <div className="grid gap-6 sm:grid-cols-2">
                {group.fields.map((field) => {
                  const id = `calc-${field.key}`;
                  const [min, max] = LIMITS[field.key];
                  return (
                    <div key={field.key}>
                      <label htmlFor={id} className="block text-sm font-semibold text-slate-900">{field.label}</label>
                      <div className="mt-2 flex items-center rounded-xl border border-slate-300 bg-white focus-within:border-[#1a4e94] focus-within:ring-2 focus-within:ring-blue-100">
                        {field.prefix && <span className="pl-3 text-slate-500" aria-hidden="true">{field.prefix}</span>}
                        <input
                          id={id}
                          name={field.key}
                          type="number"
                          inputMode="decimal"
                          min={min}
                          max={max}
                          step={field.step}
                          value={draft[field.key]}
                          onChange={(event) => update(field.key, event.target.value)}
                          aria-describedby={`${id}-help`}
                          className="w-full min-w-0 rounded-xl bg-transparent px-3 py-2.5 text-slate-950 outline-none"
                        />
                        {field.suffix && <span className="pr-3 text-sm text-slate-500" aria-hidden="true">{field.suffix}</span>}
                      </div>
                      <p id={`${id}-help`} className="mt-1.5 text-xs leading-5 text-slate-500">{field.help}</p>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}
          <button type="button" onClick={() => { setCopied(false); setEdited(true); setDraft(toDraft(DEFAULT_INPUTS)); }} className="inline-flex min-h-[40px] items-center text-sm font-semibold text-[#1a4e94] underline underline-offset-4">Reset to example values</button>
        </form>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <p role="status" className="sr-only">{`${formatHours(result.manualHours)} a month today. Net monthly saving ${formatMoney(result.netMonthly)}. Payback: ${formatPayback(result.paybackMonths)}.`}</p>
          <div className="rounded-3xl bg-[radial-gradient(circle_at_top_right,_#18356d_0,_#0a192f_45%,_#071525_100%)] p-7 text-white">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-blue-200">Today</h2>
            <p className="mt-3 text-4xl font-semibold tabular-nums">{formatHours(result.manualHours)}</p>
            <p className="mt-1 text-slate-300">a month, costing about <strong className="text-white">{formatMoney(result.manualCost)}</strong></p>

            <dl className="mt-7 space-y-4 border-t border-white/10 pt-6 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Hours saved per month</dt><dd className="font-semibold tabular-nums">{formatHours(result.hoursSaved)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Net monthly saving</dt><dd className={`font-semibold tabular-nums ${result.netMonthly < 0 ? 'text-amber-300' : ''}`}>{formatMoney(result.netMonthly)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-300">Payback period</dt><dd className="font-semibold tabular-nums">{formatPayback(result.paybackMonths)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-slate-300">First-year net</dt><dd className={`font-semibold tabular-nums ${result.firstYearNet < 0 ? 'text-amber-300' : ''}`}>{formatMoney(result.firstYearNet)}</dd></div>
            </dl>

            {!result.pays && (
              <p className="mt-6 rounded-xl bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
                {result.grossSavings === 0
                  ? 'With these numbers there are no savings from time, so automation does not pay back on time alone.'
                  : 'With these numbers, upkeep costs more than the time saved, so automation alone does not pay back. The case would rest on accuracy or speed instead.'}
              </p>
            )}

            <button type="button" onClick={copyLink} className="mt-6 w-full rounded-xl border border-white/20 px-4 py-2.5 text-sm font-semibold hover:bg-white/10">
              {copied ? 'Link copied' : 'Copy a link to these numbers'}
            </button>
          </div>
          <p className="mt-4 px-1 text-xs leading-5 text-slate-500">An estimate from your inputs, not a quote. It leaves out harder-to-price benefits, such as fewer reporting errors and faster answers.</p>
        </aside>
      </div>

      <section className="mt-14 grid gap-10 border-t border-slate-200 pt-12 lg:grid-cols-2">
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">How the numbers work</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li><strong className="text-slate-900">Manual hours</strong> = reports × hours each × runs per month ({formatHours(result.prepHours)}), plus reconciling time ({formatHours(input.fixHoursPerMonth)}).</li>
            <li><strong className="text-slate-900">Hours saved</strong> = manual hours × {input.automationShare}%.</li>
            <li><strong className="text-slate-900">Net monthly saving</strong> = hours saved × hourly cost, minus monthly upkeep.</li>
            <li><strong className="text-slate-900">Payback</strong> = build cost ÷ net monthly saving.</li>
          </ul>
        </div>
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">Before you trust the result</h2>
          <ul className="mt-5 space-y-3 leading-7 text-slate-600">
            <li>Time a few real report runs instead of guessing. Estimates are usually low because people forget the checking.</li>
            <li>Reconciling time is the hidden cost. If two reports disagree every month, read <Link to="/blog/why-shopify-finance-marketing-report-different-revenue" className="text-[#1a4e94] underline underline-offset-4">why teams report different revenue</Link>.</li>
            <li>Automation removes copying, not judgment. <Link to="/blog/why-reliable-ecommerce-reporting-takes-time" className="text-[#1a4e94] underline underline-offset-4">Why reliable reporting takes time</Link> covers what still needs a person.</li>
          </ul>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/ecommerce" className="font-semibold text-[#1a4e94] underline underline-offset-4">Ecommerce reporting help</Link>
            <Link to="/services/snowflake-consulting" className="font-semibold text-[#1a4e94] underline underline-offset-4">Snowflake consulting</Link>
            <Link to="/resources" className="font-semibold text-[#1a4e94] underline underline-offset-4">More resources</Link>
          </div>
        </div>
      </section>
    </BlogLayout>
  );
}
